/**
 * Global search — the reference's omnisearch (Search capture, Step 84):
 * one box in the top bar (`/` or ⌘K to focus), a grouped dropdown of the top
 * 3 per entity with "View all (N)", recent searches while empty, and the
 * matched term highlighted. Sections are the entities that exist today —
 * Tickets, Service items, Requesters, Agents — and Solutions joins as one
 * more group when the KB lands.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, LayoutGrid, Search, Ticket, User, Users } from 'lucide-react';
import { ticketsApi } from '../../features/tickets/api';
import { CatalogueIcon } from '../../features/fields/catalogueIcons';
import { Highlight } from '../ui/highlight';
import { qk } from '../../lib/queryClient';
import { cn } from '../../lib/utils';
import { NO_AUTOFILL } from '../ui/input';

const RECENT_KEY = 'globalSearch.recent';
function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((x) => typeof x === 'string').slice(0, 8) : [];
  } catch {
    return [];
  }
}
function pushRecent(q: string) {
  try {
    const next = [q, ...readRecents().filter((r) => r !== q)].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* private windows throw; recents are a convenience */
  }
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // URL state on purpose: the command palette's own query. The palette is an
  // overlay — it opens, does one job and closes; restoring it from a URL would
  // drop somebody into a search box with no page behind it.
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [recents, setRecents] = useState<string[]>(() => readRecents());

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // `/` (the reference's shortcut) and ⌘K both focus the box — unless the
  // keystroke belongs to an input the person is already typing in.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
      if ((e.key === '/' && !typing) || (e.key === 'k' && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, []);

  const { data } = useQuery({
    queryKey: qk.globalSearch(debounced),
    queryFn: () => ticketsApi.globalSearch(debounced),
    enabled: open && debounced.length >= 2,
  });

  function go(path: string) {
    if (debounced.length >= 2) {
      pushRecent(debounced);
      setRecents(readRecents());
    }
    setOpen(false);
    navigate(path);
  }

  const showRecents = open && debounced.length < 2 && recents.length > 0;
  const showResults = open && debounced.length >= 2 && !!data;
  const sections = useMemo(() => {
    if (!data) return [];
    return [
      { key: 'tickets', label: 'Tickets', icon: Ticket, group: data.tickets },
      { key: 'items', label: 'Service items', icon: LayoutGrid, group: data.items },
      { key: 'articles', label: 'Solutions', icon: BookOpen, group: data.articles },
      { key: 'requesters', label: 'Requesters', icon: User, group: data.requesters },
      { key: 'agents', label: 'Agents', icon: Users, group: data.agents },
    ].filter((s) => (s.group?.total ?? 0) > 0);
  }, [data]);

  return (
    <div ref={rootRef} className="relative hidden w-full max-w-xl sm:block">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        {...NO_AUTOFILL}
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
          }
          if (e.key === 'Enter') {
            // The LIVE value, not `debounced` — a fast Enter within the 250ms
            // window used to search the previous term, or nothing at all
            // (review finding, 2026-09-03).
            const live = q.trim();
            if (live.length >= 2) {
              pushRecent(live);
              setRecents(readRecents());
              setOpen(false);
              navigate(`/search?q=${encodeURIComponent(live)}`);
            }
          }
        }}
        placeholder="Search tickets, services, people…"
        className={cn(
          'h-9 w-full rounded-md border bg-background pl-8 pr-10 text-sm',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      />
      <kbd className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded border bg-muted px-1.5 text-[10px] text-muted-foreground">
        /
      </kbd>

      {(showRecents || showResults) && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-[70vh] overflow-y-auto rounded-md border bg-popover p-2 shadow-md">
          {showRecents && (
            <div>
              <div className="mb-1 flex items-center justify-between px-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Recent searches
                </p>
                <button
                  className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  onClick={() => {
                    try {
                      localStorage.removeItem(RECENT_KEY);
                    } catch {
                      /* ignore */
                    }
                    setRecents([]);
                  }}
                >
                  Clear all
                </button>
              </div>
              {recents.map((r) => (
                <button
                  key={r}
                  className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                  onClick={() => {
                    setQ(r);
                    inputRef.current?.focus();
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          )}

          {showResults && sections.length === 0 && (
            <p className="px-2 py-3 text-sm text-muted-foreground">No matches for “{debounced}”.</p>
          )}

          {showResults &&
            sections.map((s) => (
              <div key={s.key} className="mb-2 last:mb-0">
                <p className="mb-1 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <s.icon className="h-3.5 w-3.5" /> {s.label}
                </p>
                <div className="rounded-md border">
                  {s.key === 'tickets' &&
                    data!.tickets!.entries.map((t) => (
                      <button
                        key={t.id}
                        className="block w-full border-b px-2.5 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                        onClick={() => go(`/tickets/${t.id}`)}
                      >
                        <Highlight text={t.subject} term={debounced} />{' '}
                        <span className="text-muted-foreground">
                          (#{t.type === 'service_request' ? 'SR' : 'INC'}-{t.number})
                        </span>
                      </button>
                    ))}
                  {s.key === 'articles' &&
                    data!.articles!.entries.map((a) => (
                      <button
                        key={a.id}
                        className="flex w-full items-center gap-2 border-b px-2.5 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                        onClick={() => go(`/kb?article=${a.id}`)}
                      >
                        <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0">
                          <span className="block truncate">
                            <Highlight text={a.title} term={debounced} />
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">{a.folderName}</span>
                        </span>
                      </button>
                    ))}
                  {s.key === 'articles' && data!.articles!.total > 3 && (
                    <button
                      className="block w-full px-2.5 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                      onClick={() => go(`/search?q=${encodeURIComponent(debounced)}&tab=articles`)}
                    >
                      View all ({data!.articles!.total})
                    </button>
                  )}
                  {s.key === 'items' &&
                    data!.items!.entries.map((it) => (
                      <button
                        key={it.id}
                        className="flex w-full items-center gap-2 border-b px-2.5 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                        onClick={() => go(`/catalogue?item=${it.id}`)}
                      >
                        <CatalogueIcon name={it.icon} className="h-4 w-4 shrink-0 text-brand" />
                        <Highlight text={it.name} term={debounced} />
                      </button>
                    ))}
                  {(s.key === 'requesters' || s.key === 'agents') &&
                    (s.key === 'requesters' ? data!.requesters : data!.agents)!.entries.map((p) => (
                      // Step 53/130 — these used to be dead rows, with a note
                      // saying so "until a person page exists". It does now.
                      <button
                        key={p.id}
                        className="block w-full border-b px-2.5 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                        onClick={() => go(`/people/${p.id}`)}
                      >
                        <Highlight text={p.name ?? p.email} term={debounced} />{' '}
                        <span className="text-muted-foreground">
                          – <Highlight text={p.email} term={debounced} />
                        </span>
                      </button>
                    ))}
                  {(s.key === 'requesters' || s.key === 'agents') &&
                    (s.key === 'requesters' ? data!.requesters : data!.agents)!.total > 3 && (
                      <button
                        className="block w-full px-2.5 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                        onClick={() => go(`/search?q=${encodeURIComponent(debounced)}&tab=${s.key}`)}
                      >
                        View all ({(s.key === 'requesters' ? data!.requesters : data!.agents)!.total})
                      </button>
                    )}
                  {s.key === 'tickets' && data!.tickets!.total > 3 && (
                    <button
                      className="block w-full px-2.5 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                      onClick={() => go(`/search?q=${encodeURIComponent(debounced)}&tab=tickets`)}
                    >
                      View all ({data!.tickets!.total})
                    </button>
                  )}
                  {s.key === 'items' && data!.items!.total > 3 && (
                    <button
                      className="block w-full px-2.5 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                      onClick={() => go(`/search?q=${encodeURIComponent(debounced)}&tab=items`)}
                    >
                      View all ({data!.items!.total})
                    </button>
                  )}
                </div>
              </div>
            ))}
          {showResults && sections.length > 0 && (
            <button
              className="mt-1 block w-full rounded px-2 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={() => go(`/search?q=${encodeURIComponent(debounced)}`)}
            >
              See more results for “{debounced}” »
            </button>
          )}
        </div>
      )}
    </div>
  );
}
